import type { FoundationTokens } from '../../tokens/Foundation/Foundation';
import type { SemanticTokenName, SemanticTokens } from '../../tokens/Semantic/Semantic';
import { ContrastPicker } from '../ContrastPicker/ContrastPicker';
import { HexOklch } from '../HexOklch/HexOklch';
import { PaletteEngine } from '../PaletteEngine/PaletteEngine';
import { ShadowCascadeBuilder } from '../ShadowCascadeBuilder/ShadowCascadeBuilder';
import type { ThemeDeclaration } from '../Theme';

interface ISemanticMapper {
  map(
    foundation: { dark: FoundationTokens; light: FoundationTokens },
    decl: ThemeDeclaration
  ): { dark: SemanticTokens; light: SemanticTokens };
}

type Mode = 'light' | 'dark';

/**
 * A single semantic-token mapping. Strings starting with `--` are foundation
 * token keys (eg: `--brand-500`); any other string is emitted as a literal
 * (eg: `'transparent'`, `'none'`, `'oklch(0 0 0 / 0.5)'`). When a token needs
 * a different foundation stop per mode, use `{ light, dark }`. Everything else
 * is mode-invariant — the foundation flip does the work.
 */
type Spec = string | { light: string; dark: string };

/**
 * Button fills read ~20% less saturated than the raw palette. The scale is applied to every
 * genuinely chromatic `--action-*` value; near-neutral colours (chroma at or below
 * `ACTION_NEUTRAL_CHROMA`), `transparent`, and other non-hex literals pass through untouched — so
 * white/black button text and the ghost/outline gray surfaces stay exactly as the palette emits them.
 * `vivid` themes opt out (`ACTION_CHROMA_SCALE_VIVID`): the mute is a tasteful default on a conventional
 * palette but cancels the point of a neon one. Legibility is unaffected — foregrounds are contrast-derived
 * from the final fill, and unmuted vivid fills clear 10–14.5:1 against a 4.5:1 requirement.
 */
const ACTION_CHROMA_SCALE = 0.8;
const ACTION_CHROMA_SCALE_VIVID = 1;
const ACTION_NEUTRAL_CHROMA = 0.03;

/**
 * Foregrounds sitting *on* a brand/accent fill, mapped to the token holding that fill. Each is resolved by
 * measured contrast against the fill's final value rather than hard-coded white, because a `vivid` brand fill
 * can be light enough that white is unreadable. Runs after the main pass so it measures the post-mute value.
 * Most pairs reproduce the previous white, leaving standard themes unchanged. Three move because the
 * hard-coded value already failed AA: the two `*-fg-active` tokens (white on the lighter pressed fill,
 * 3.43–3.78:1; black scores 5.56–6.12:1) and `--accent-on-bg` (hard-coded `--gray-0`, which is BLACK in dark
 * mode, 3.84:1 where white scores 5.47:1).
 * Feedback foregrounds are deliberately absent — those families always use the standard curve.
 */
const CONTRAST_PAIRED_FG: Readonly<Record<string, SemanticTokenName>> = {
  '--accent-on-bg': '--accent-bg',
  '--action-primary-fg': '--action-primary-bg',
  '--action-primary-fg-active': '--action-primary-bg-active',
  '--action-primary-fg-hover': '--action-primary-bg-hover',
  '--action-secondary-fg': '--action-secondary-bg',
  '--action-secondary-fg-active': '--action-secondary-bg-active',
  '--action-secondary-fg-hover': '--action-secondary-bg-hover',
  '--brand-on-bg': '--brand-bg',
  '--icon-on-accent': '--accent-bg',
  '--icon-on-brand': '--brand-bg',
  '--nav-item-fg-active': '--nav-item-bg-active',
  '--text-on-accent': '--accent-bg',
  '--text-on-brand': '--brand-bg'
};

/**
 * Alphabetic source-of-truth for every semantic token (201 of 210). The
 * remaining 9 (`--chart-1..8`, `--shadow-inset`) are computed in `_resolveSemantic`.
 * `--chart-diverging-1/2/4/5` have static defaults here and are overridden when
 * `decl.diverging` is set.
 */
export const SEMANTIC_MAP: Readonly<
  Record<
    Exclude<
      SemanticTokenName,
      | '--chart-1'
      | '--chart-2'
      | '--chart-3'
      | '--chart-4'
      | '--chart-5'
      | '--chart-6'
      | '--chart-7'
      | '--chart-8'
      | '--shadow-inset'
    >,
    Spec
  >
> = {
  '--accent-bg': '--accent-500',
  '--accent-bg-subtle': { light: '--accent-50', dark: '--accent-100' },
  '--accent-border': '--accent-600',
  '--accent-fg': '--accent-600',
  '--accent-on-bg': '--gray-0',
  '--action-destructive-bg': { light: '--danger-600', dark: '--danger-400' },
  '--action-destructive-bg-active': { light: '--danger-400', dark: '--danger-600' },
  '--action-destructive-bg-disabled': { light: '--gray-100', dark: '--gray-250' },
  '--action-destructive-bg-hover': { light: '--danger-500', dark: '--danger-500' },
  '--action-destructive-border': { light: '--danger-600', dark: '--danger-400' },
  '--action-destructive-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-destructive-border-hover': '--danger-400',
  '--action-destructive-fg': { light: '--gray-0', dark: '--gray-1000' },
  '--action-destructive-fg-active': { light: '--gray-0', dark: '--gray-1000' },
  '--action-destructive-fg-disabled': '--gray-400',
  '--action-destructive-fg-hover': { light: '--gray-0', dark: '--gray-1000' },
  '--action-destructive-outline-bg': 'transparent',
  '--action-destructive-outline-bg-active': { light: '--danger-400', dark: '--danger-500' },
  '--action-destructive-outline-bg-disabled': 'transparent',
  '--action-destructive-outline-bg-hover': { light: '--danger-500', dark: '--danger-400' },
  '--action-destructive-outline-border': { light: '--danger-500', dark: '--danger-400' },
  '--action-destructive-outline-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-destructive-outline-border-hover': '--danger-400',
  '--action-destructive-outline-fg': { light: '--danger-500', dark: '--danger-400' },
  '--action-destructive-outline-fg-active': { light: '--gray-0', dark: '--gray-1000' },
  '--action-destructive-outline-fg-disabled': '--gray-400',
  '--action-destructive-outline-fg-hover': { light: '--gray-0', dark: '--gray-1000' },
  '--action-ghost-bg': 'transparent',
  '--action-ghost-bg-active': { light: '--gray-150', dark: '--gray-300' },
  '--action-ghost-bg-disabled': 'transparent',
  '--action-ghost-bg-hover': { light: '--gray-100', dark: '--gray-250' },
  '--action-ghost-border': 'transparent',
  '--action-ghost-border-disabled': 'transparent',
  '--action-ghost-border-hover': 'transparent',
  '--action-ghost-fg': '--gray-900',
  '--action-ghost-fg-active': '--gray-900',
  '--action-ghost-fg-disabled': '--gray-400',
  '--action-ghost-fg-hover': '--gray-900',
  '--action-link-bg': 'transparent',
  '--action-link-bg-active': 'transparent',
  '--action-link-bg-disabled': 'transparent',
  '--action-link-bg-hover': 'transparent',
  '--action-link-border': 'transparent',
  '--action-link-border-disabled': 'transparent',
  '--action-link-border-hover': 'transparent',
  '--action-link-fg': { light: '--brand-500', dark: '--brand-600' },
  '--action-link-fg-active': { light: '--brand-700', dark: '--brand-800' },
  '--action-link-fg-disabled': '--gray-400',
  '--action-link-fg-hover': { light: '--brand-600', dark: '--brand-700' },
  '--action-outline-bg': 'transparent',
  /**
   * DARK VALUES ARE ONE LADDER STOP HIGHER THAN THEY LOOK LIKE THEY SHOULD, and that is the fix, not a
   * typo. An outline button paints its hover/active fill ONTO `--surface-default`, which in dark mode is
   * `--gray-200` — so the old dark `bg-hover` of `--gray-200` was the very surface it sat on and the hover
   * was INVISIBLE app-wide (dL 0.000), while `bg-active` at `--gray-250` gave dL 0.052, under half the
   * light-mode press contrast of 0.126. Light mode never showed it: there the ladder runs the other way and
   * `--surface-default` is `--gray-50`, clear of both. The same collision is what made `ContentHeader`'s
   * segment frame borderless in dark (see `_segmentFrame.scss`) — `--gray-200` is the dark plane's
   * everything-surface and nothing drawn ON that plane may resolve to it.
   * Stepping hover to `--gray-250` and active to `--gray-350` restores a visible two-stage press in dark
   * (dL 0.052 then 0.156) and leaves LIGHT MODE BYTE-IDENTICAL.
   */
  '--action-outline-bg-active': { light: '--gray-250', dark: '--gray-400' },
  '--action-outline-bg-disabled': 'transparent',
  '--action-outline-bg-hover': { light: '--gray-150', dark: '--gray-300' },
  '--action-outline-border': { light: '--gray-500', dark: '--gray-400' },
  '--action-outline-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-outline-border-hover': { light: '--gray-500', dark: '--gray-400' },
  '--action-outline-fg': { light: '--gray-1000', dark: '--gray-1000' },
  '--action-outline-fg-active': { light: '--gray-1000', dark: '--gray-1000' },
  '--action-outline-fg-disabled': '--gray-400',
  '--action-outline-fg-hover': { light: '--gray-1000', dark: '--gray-1000' },
  '--action-primary-bg': { light: '--brand-600', dark: '--brand-400' },
  '--action-primary-bg-active': { light: '--brand-400', dark: '--brand-600' },
  '--action-primary-bg-disabled': { light: '--gray-100', dark: '--gray-250' },
  '--action-primary-bg-hover': { light: '--brand-500', dark: '--brand-500' },
  '--action-primary-border': { light: '--brand-600', dark: '--brand-400' },
  '--action-primary-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-primary-border-hover': { light: '--brand-500', dark: '--brand-500' },
  '--action-primary-fg': { light: '--gray-0', dark: '--gray-1000' },
  '--action-primary-fg-active': { light: '--gray-0', dark: '--gray-1000' },
  '--action-primary-fg-disabled': '--gray-400',
  '--action-primary-fg-hover': { light: '--gray-0', dark: '--gray-1000' },
  '--action-secondary-bg': { light: '--accent-600', dark: '--accent-400' },
  '--action-secondary-bg-active': { light: '--accent-400', dark: '--accent-600' },
  '--action-secondary-bg-disabled': { light: '--gray-100', dark: '--gray-250' },
  '--action-secondary-bg-hover': { light: '--accent-500', dark: '--accent-500' },
  '--action-secondary-border': { light: '--accent-600', dark: '--accent-400' },
  '--action-secondary-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-secondary-border-hover': { light: '--accent-500', dark: '--accent-500' },
  '--action-secondary-fg': { light: '--gray-0', dark: '--gray-1000' },
  '--action-secondary-fg-active': { light: '--gray-0', dark: '--gray-1000' },
  '--action-secondary-fg-disabled': '--gray-400',
  '--action-secondary-fg-hover': { light: '--gray-0', dark: '--gray-1000' },
  '--action-tertiary-bg': { light: '--brand-100', dark: '--brand-200' },
  '--action-tertiary-bg-active': { light: '--brand-300', dark: '--brand-400' },
  '--action-tertiary-bg-disabled': { light: '--gray-100', dark: '--gray-250' },
  '--action-tertiary-bg-hover': { light: '--brand-200', dark: '--brand-300' },
  '--action-tertiary-border': 'transparent',
  '--action-tertiary-border-disabled': { light: '--gray-200', dark: '--gray-350' },
  '--action-tertiary-border-hover': 'transparent',
  '--action-tertiary-fg': '--brand-800',
  '--action-tertiary-fg-active': '--brand-800',
  '--action-tertiary-fg-disabled': '--gray-400',
  '--action-tertiary-fg-hover': '--brand-800',
  '--border-default': { light: '--gray-200', dark: '--gray-250' },
  '--border-divider': { light: '--gray-200', dark: '--gray-350' },
  '--border-on-brand': '--brand-700',
  '--border-on-feedback': '--gray-0',
  '--border-on-sidebar': { light: '--gray-250', dark: '--gray-350' },
  '--border-strong': '--gray-400',
  '--border-subtle': '--gray-100',
  '--border-tooltip': { light: '--gray-700', dark: '--gray-350' },
  '--brand-bg': '--brand-500',
  '--brand-bg-subtle': { light: '--brand-50', dark: '--brand-100' },
  '--brand-border': '--brand-600',
  '--brand-fg': '--brand-600',
  '--brand-on-bg': { light: '--gray-0', dark: '--gray-1000' },
  '--chart-axis': '--gray-500',
  '--chart-diverging-1': '--danger-700',
  '--chart-diverging-2': '--danger-400',
  '--chart-diverging-3': '--gray-300',
  '--chart-diverging-4': '--success-400',
  '--chart-diverging-5': '--success-700',
  '--chart-grid': { light: '--gray-200', dark: '--gray-250' },
  '--chart-negative': '--danger-600',
  '--chart-positive': '--success-600',
  '--chart-sequential-1': '--brand-100',
  '--chart-sequential-2': '--brand-300',
  '--chart-sequential-3': '--brand-500',
  '--chart-sequential-4': '--brand-700',
  '--chart-sequential-5': '--brand-900',
  '--chart-tooltip-bg': '--gray-0',
  '--chart-tooltip-fg': '--gray-900',
  '--code-bg': '--gray-100',
  /** The code editor's own background: `--code-bg` in light; in dark a fixed blue-black every palette's editor
   *  shares, deeper than the gray ramp, so a long editing session sits on the same ground whatever the palette. */
  '--code-editor-bg': { light: '--gray-100', dark: '#06070d' },
  /** Dark drops editor text to the `--text-secondary` stop: near-white on near-black glares across a long document. */
  '--code-fg': { light: '--gray-900', dark: '--gray-700' },
  '--feedback-danger-bg': { light: '--danger-600', dark: '--danger-400' },
  '--feedback-danger-bg-subtle': '--danger-50',
  '--feedback-danger-border': { light: '--danger-600', dark: '--danger-400' },
  '--feedback-danger-fg': '--danger-700',
  '--feedback-danger-on-bg': { light: '--gray-0', dark: '--gray-1000' },
  '--feedback-info-bg': { light: '--info-600', dark: '--info-400' },
  '--feedback-info-bg-subtle': '--info-50',
  '--feedback-info-border': { light: '--info-600', dark: '--info-400' },
  '--feedback-info-fg': '--info-700',
  '--feedback-info-on-bg': { light: '--gray-0', dark: '--gray-1000' },
  '--feedback-success-bg': { light: '--success-600', dark: '--success-400' },
  '--feedback-success-bg-subtle': '--success-50',
  '--feedback-success-border': { light: '--success-600', dark: '--success-400' },
  '--feedback-success-fg': '--success-700',
  '--feedback-success-on-bg': { light: '--gray-0', dark: '--gray-1000' },
  /* Theme-invariant green confirmation (login "verified" beat). Emitted as literals so they resolve against the mode-invariant palette :root block rather than the theme-tunable --success-* family; `on-bg` is a fixed white checkmark for guaranteed contrast in both modes. See VerifiedTokenName. */
  '--feedback-verified-bg': 'var(--palette-green-600)',
  '--feedback-verified-on-bg': 'oklch(1 0 0)',
  '--feedback-warning-bg': { light: '--warning-600', dark: '--warning-400' },
  '--feedback-warning-bg-subtle': '--warning-50',
  '--feedback-warning-border': { light: '--warning-600', dark: '--warning-400' },
  '--feedback-warning-fg': '--warning-700',
  '--feedback-warning-on-bg': { light: '--gray-0', dark: '--gray-1000' },
  '--icon-default': '--gray-700',
  '--icon-disabled': { light: '--gray-300', dark: '--gray-350' },
  '--icon-muted': '--gray-500',
  '--icon-on-accent': { light: '--gray-0', dark: '--gray-1000' },
  '--icon-on-brand': { light: '--gray-0', dark: '--gray-1000' },
  '--icon-on-feedback': { light: '--gray-0', dark: '--gray-1000' },
  '--nav-item-bg': 'transparent',
  '--nav-item-bg-active': '--brand-500',
  '--nav-item-bg-hover': { light: '--gray-200', dark: '--gray-300' },
  '--nav-item-fg': '--gray-800',
  '--nav-item-fg-active': { light: '--gray-0', dark: '--gray-1000' },
  '--ring-focus': '--brand-500',
  '--ring-focus-danger': '--danger-500',
  '--ring-focus-inverse': '--gray-1000',
  '--ring-focus-success': '--success-500',
  '--scrollbar-thumb': { light: '--gray-300', dark: '--gray-350' },
  '--scrollbar-thumb-hover': '--gray-500',
  '--scrollbar-track': 'transparent',
  '--shadow-card': '--shadow-sm',
  '--shadow-modal': '--shadow-xl',
  '--shadow-overlay': '--shadow-lg',
  '--shadow-popover': '--shadow-md',
  '--shadow-raised': '--shadow-md',
  '--shadow-tooltip': '--shadow-sm',
  '--skeleton-bg': { light: '--gray-100', dark: '--gray-300' },
  '--state-disabled-bg': '--gray-100',
  '--state-disabled-border': { light: '--gray-200', dark: '--gray-350' },
  '--state-disabled-fg': '--gray-400',
  '--surface-backdrop': 'oklch(0 0 0 / 0.5)',
  /**
   * The dark ladder sits above the ramp's crushed bottom — `--gray-0` and `--gray-50` are BOTH `#000000` in every
   * dark theme. The canvas stays at `--gray-100`, a near-black floor, and every surface above it (sunken/sidebar
   * `--gray-200`, default `--gray-250`, overlay `--gray-300`, raised/hover `--gray-350`) sits two-plus stops clear
   * of it so panels, menus and dialogs read as lit surfaces rather than shades of the void. Every dark fill and
   * border painted ON those surfaces (hovers, disabled fills, borders, skeletons) moved up with them in one step,
   * so each keeps its separation from the surface under it. LIGHT MODE IS UNTOUCHED throughout: its ramp is not
   * crushed, and lifting it would grey out the page.
   */
  '--surface-canvas': { light: '--gray-0', dark: '--gray-100' },
  '--surface-default': { light: '--gray-50', dark: '--gray-250' },
  '--surface-hover': { light: '--gray-100', dark: '--gray-350' },
  '--surface-hover-brand': 'color-mix(in oklab, var(--surface-default), var(--brand-500) 6%)',
  '--surface-overlay': { light: '--gray-0', dark: '--gray-300' },
  '--surface-pressed': { light: '--gray-150', dark: '--gray-400' },
  '--surface-raised': { light: '--gray-0', dark: '--gray-350' },
  '--surface-row-stripe': { light: '--gray-50', dark: '--gray-100' },
  '--surface-selected-bg': { light: '--brand-500', dark: '--brand-400' },
  '--surface-selected-fg': { light: '--gray-0', dark: '--gray-1000' },
  /**
   * Plain gray on purpose, with no brand mix, so the app chrome reads as the same neutral as the Settings nav.
   * Components step it one stop per mode through `@blufin/react`'s `sidebarChrome.scss`.
   */
  '--surface-sidebar': { light: '--gray-50', dark: '--gray-200' },
  '--surface-sunken': { light: '--gray-0', dark: '--gray-200' },
  /** Dark in both modes; in dark it matches `--surface-default`, so `--border-tooltip` keeps it legible over panels. */
  '--surface-tooltip': { light: '--gray-750', dark: '--gray-250' },
  '--text-disabled': '--gray-400',
  '--text-inverted': '--gray-50',
  '--text-link': '--brand-600',
  '--text-link-decoration': 'none',
  '--text-link-decoration-hover': 'underline',
  '--text-link-hover': '--brand-700',
  '--text-muted': '--gray-500',
  '--text-on-accent': { light: '--gray-0', dark: '--gray-1000' },
  '--text-on-brand': { light: '--gray-0', dark: '--gray-1000' },
  '--text-on-danger': { light: '--gray-0', dark: '--gray-1000' },
  '--text-on-info': { light: '--gray-0', dark: '--gray-1000' },
  '--text-on-sidebar': '--gray-900',
  '--text-on-success': { light: '--gray-0', dark: '--gray-1000' },
  '--text-on-tooltip': { light: '--gray-100', dark: '--gray-900' },
  '--text-on-warning': { light: '--gray-0', dark: '--gray-1000' },
  '--text-primary': '--gray-900',
  '--text-secondary': '--gray-700',
  '--text-selection-bg': '--brand-200',
  '--text-selection-fg': '--gray-1000'
};

/**
 * Stop remaps applied only when `decl.intensity === 'vivid'`, for two reasons each entry names:
 * **Aesthetic** — at the vivid anchor stop 600 is past the chroma peak and reads muddy, so identity fills
 * move to the peak at 500. **Contrast** — vivid pushes brand light, so brand-coloured text/borders on the
 * canvas stop clearing 4.5:1 in light mode and move darker; dark mode is untouched, as the curve mirrors.
 * Every contrast entry comes from a measured failure in `intensity.test.ts`. Foregrounds on these fills need
 * no entry — `CONTRAST_PAIRED_FG` derives them.
 */
const VIVID_SEMANTIC_OVERRIDES: Readonly<Partial<Record<keyof typeof SEMANTIC_MAP, Spec>>> = {
  /* Aesthetic: 500 is the chroma peak — the neon stop. 600 is the muddy shoulder. */
  '--action-primary-bg': { light: '--brand-500', dark: '--brand-400' },
  /* Contrast: brand text on canvas. Light-mode 600 falls to ~2.6:1; 700 clears 4.5:1. */
  '--action-link-fg': { light: '--brand-700', dark: '--brand-600' },
  '--action-link-fg-active': { light: '--brand-800', dark: '--brand-800' },
  '--action-link-fg-hover': { light: '--brand-800', dark: '--brand-700' },
  '--brand-fg': { light: '--brand-700', dark: '--brand-600' },
  '--text-link': { light: '--brand-700', dark: '--brand-600' },
  '--text-link-hover': { light: '--brand-800', dark: '--brand-700' }
};

export const SemanticMapper: ISemanticMapper = {
  /**
   * Resolve semantic tokens for both modes.
   * @param foundation Foundation tokens per mode (from PaletteEngine).
   * @param decl Theme declaration (chart seeds come from `brand` and `accent`).
   * @returns Semantic tokens per mode.
   */
  map(
    foundation: { dark: FoundationTokens; light: FoundationTokens },
    decl: ThemeDeclaration
  ): { dark: SemanticTokens; light: SemanticTokens } {
    return {
      dark: _resolveSemantic(foundation.dark, decl, 'dark'),
      light: _resolveSemantic(foundation.light, decl, 'light')
    };
  }
};

const _categoricalHexes = (decl: ThemeDeclaration): readonly string[] => {
  const brand = HexOklch.hexToOklch(decl.brand);
  if (!brand) throw new Error(`SemanticMapper: invalid brand hex "${decl.brand}"`);
  const accent = HexOklch.hexToOklch(decl.accent);
  if (!accent) throw new Error(`SemanticMapper: invalid accent hex "${decl.accent}"`);
  const out: string[] = [];
  /** Anchored mirror — categorical swatches share a mode-invariant lightness; 0.68 reads vivid on both canvases. */
  const l = 0.68;
  for (const seed of [brand, accent] as const) {
    const c = Math.max(seed.c, 0.18);
    for (const offset of [0, 90, 180, 270]) {
      const h = (((seed.h + offset) % 360) + 360) % 360;
      out.push(HexOklch.oklchToHex({ c, h, l }));
    }
  }
  return out;
};

/**
 * Tone a resolved `--action-*` colour down to `ACTION_CHROMA_SCALE` of its chroma, keeping lightness
 * and hue intact so contrast (and white/black text legibility) is unchanged. Neutral values
 * (chroma ≤ `ACTION_NEUTRAL_CHROMA`) and non-hex literals (`transparent`, …) are returned as-is.
 * @param value Resolved semantic colour (hex or literal).
 * @returns The desaturated hex, or `value` unchanged when it is neutral / non-hex.
 */
const _muteActionChroma = (value: string, scale: number): string => {
  const oklch = HexOklch.hexToOklch(value);
  if (!oklch || oklch.c <= ACTION_NEUTRAL_CHROMA) return value;
  return HexOklch.oklchToHex({ ...oklch, c: oklch.c * scale });
};

const _resolve = (spec: Spec, mode: Mode, foundation: FoundationTokens): string => {
  const ref = typeof spec === 'string' ? spec : spec[mode];
  if (!ref.startsWith('--')) return ref;
  const value = foundation[ref as keyof FoundationTokens];
  if (value === undefined) throw new Error(`SemanticMapper: missing foundation token "${ref}"`);
  return value;
};

const _resolveSemantic = (foundation: FoundationTokens, decl: ThemeDeclaration, mode: Mode): SemanticTokens => {
  const result: Partial<SemanticTokens> = {};
  const isVivid = decl.intensity === 'vivid';
  const overrides = isVivid ? VIVID_SEMANTIC_OVERRIDES : {};
  const actionChromaScale = isVivid ? ACTION_CHROMA_SCALE_VIVID : ACTION_CHROMA_SCALE;
  for (const [name, spec] of Object.entries(SEMANTIC_MAP) as Array<[keyof typeof SEMANTIC_MAP, Spec]>) {
    const resolved = _resolve(overrides[name] ?? spec, mode, foundation);
    result[name] = name.startsWith('--action-') ? _muteActionChroma(resolved, actionChromaScale) : resolved;
  }
  /** Contrast-derived foregrounds, after the main pass so `--action-*` fills are measured post-mute. White is listed first so a tie keeps the historical value. */
  const white = _resolve({ dark: '--gray-1000', light: '--gray-0' }, mode, foundation);
  const black = _resolve({ dark: '--gray-0', light: '--gray-1000' }, mode, foundation);
  for (const [name, fillToken] of Object.entries(CONTRAST_PAIRED_FG)) {
    const fill = result[fillToken];
    if (fill === undefined) throw new Error(`SemanticMapper: missing fill "${fillToken}" for "${name}"`);
    result[name as SemanticTokenName] = ContrastPicker.pick(fill, [white, black]);
  }
  /** Computed: categorical chart hexes derived from brand + accent seeds via OKLCH rotation. */
  const categorical = _categoricalHexes(decl);
  for (let i = 0; i < 8; i++) {
    result[`--chart-${(i + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`] = categorical[i];
  }
  /** Computed: shadow-inset is mode-aware so it remains visible on dark surfaces. */
  result['--shadow-inset'] = ShadowCascadeBuilder.buildInset({ mode });
  /** Override: chart-diverging-1/2/4/5 use theme-supplied seeds when provided. */
  if (decl.diverging) {
    const negative = PaletteEngine.deriveStops(decl.diverging.negative, mode);
    const positive = PaletteEngine.deriveStops(decl.diverging.positive, mode);
    result['--chart-diverging-1'] = negative['700'];
    result['--chart-diverging-2'] = negative['400'];
    result['--chart-diverging-4'] = positive['400'];
    result['--chart-diverging-5'] = positive['700'];
  }
  return result as SemanticTokens;
};
